import { Injectable } from '@angular/core';
import { initializeApp, getApps, FirebaseApp, FirebaseOptions } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getMessaging, Messaging, isSupported } from 'firebase/messaging';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { environment } from '../../../environments/environment';

/**
 * Core Firebase Service
 * Initializes Firebase app and provides access to Firebase services
 */
@Injectable({
    providedIn: 'root'
})
export class FirebaseService {
    private app: FirebaseApp | null = null;
    private _auth: Auth | null = null;
    private _firestore: Firestore | null = null;
    private _messaging: Messaging | null = null;
    private _analytics: Analytics | null = null;

    constructor() {
        this.initializeFirebase();
    }

    refreshForCurrentAcademy(explicitUser?: any): void {
        const config = this.getFirebaseConfig(explicitUser);

        if (this.app?.options?.projectId === config?.projectId) return;

        const appName = `vult-chat-${config.projectId}`;
        const existing = getApps().find((candidate) => candidate.name === appName);
        this.app = existing || initializeApp(config, appName);
        this._auth = getAuth(this.app);
        this._firestore = getFirestore(this.app);
        console.warn(`[FirebaseService 🔄] Switched chat project to: ${config.projectId}`);
    }

    /**
     * Determine which Firebase configuration to use based on the logged-in user's academy slug.
     */
    private getFirebaseConfig(explicitUser?: any): FirebaseOptions {
        let firebaseConfig: FirebaseOptions = environment.firebase;

        if (typeof window === 'undefined') {
            return firebaseConfig;
        }

        try {
            const rawUser = localStorage.getItem('currentUser');
            let isZamalek = false;
            let isBoketto = false;

            const zamalekSlug = (environment as any).allowedAcademySlug || '';
            const bokettoSlug = (environment as any).bokettoSlug || 'bokuto-sports-academy';

            const user = explicitUser || (rawUser ? JSON.parse(rawUser) : null);

            if (user) {
                const academy = user?.academy || {};
                
                // Use main_academy_slug if present, fallback to slug
                const currentSlug = (((academy as any).main_academy_slug) || academy.slug || '').toLowerCase();
                const identity = [
                    currentSlug,
                    academy.title,
                    academy.parent?.slug,
                    academy.parent?.title,
                ].filter(Boolean).join(' ').toLowerCase();
                const academyId = Number(user?.academy_id || user?.profile?.academy_id || academy.id || 0);
                const parentId = Number(
                    academy.parent_id
                    || academy.parent?.id
                    || user?.profile?.academy?.parent_id
                    || user?.profile?.academy?.parent?.id
                    || 0
                );
                
                console.log('[FirebaseService 🔍] Evaluating Config for logged-in user:');
                console.log('[FirebaseService 🔍]   -> currentSlug:', currentSlug);
                console.log('[FirebaseService 🔍]   -> zamalekSlug:', zamalekSlug);
                console.log('[FirebaseService 🔍]   -> bokettoSlug:', bokettoSlug);

                // Check for Zamalek
                if ([140, 144].includes(academyId)
                    || [140].includes(parentId)
                    || identity.includes('zamalek')
                    || (zamalekSlug && currentSlug.includes(zamalekSlug))) {
                    isZamalek = true;
                }
                
                // Check for Boketto
                if (!isZamalek && currentSlug.includes(bokettoSlug)) {
                    isBoketto = true;
                }
                
                console.log('[FirebaseService 🔍]   -> isZamalek?', isZamalek);
                console.log('[FirebaseService 🔍]   -> isBoketto?', isBoketto);

            } else {
                // Fallback to URL checking if the user is not logged in yet
                const hostname = window.location.hostname;
                const pathname = window.location.pathname;

                if ((zamalekSlug && (hostname.includes(zamalekSlug) || pathname.includes(zamalekSlug)))
                    || hostname.includes('zamalek')
                    || pathname.includes('zamalek')) {
                    isZamalek = true;
                } else if (hostname.includes(bokettoSlug) || pathname.includes(bokettoSlug)) {
                    isBoketto = true;
                }
            }

            const zamalekConfig = (environment as any).firebaseAcademy || (environment as any).firebaseZamalek;
            const bokettoConfig = (environment as any).firebaseBoketto;

            if (isZamalek && zamalekConfig) {
                console.warn('[FirebaseService 🔍] Using Zamalek Config');
                firebaseConfig = zamalekConfig;
            } else if (isBoketto && bokettoConfig) {
                console.warn('[FirebaseService 🔍] Using Boketto Config');
                firebaseConfig = bokettoConfig;
            } else {
                console.warn('[FirebaseService 🔍] Using Default Config');
            }

        } catch (error) {
            console.warn('Failed to parse current user context for Firebase initialization:', error);
            console.warn('[FirebaseService 🔍] Using Default Config (Error fallback)');
        }

        return firebaseConfig;
    }

    /**
     * Initialize Firebase with configuration from environment
     */
    private initializeFirebase(): void {
        try {
            const firebaseConfig = this.getFirebaseConfig();

            console.warn(`[FirebaseService 🔍] Initializing Firebase with Project ID: ${firebaseConfig.projectId}`);

            // Initialize Firebase app
            this.app = initializeApp(firebaseConfig);

            // Initialize Auth
            this._auth = getAuth(this.app);

            // Initialize Firestore
            this._firestore = getFirestore(this.app);

            // Initialize Analytics (only in production and if supported)
            if (environment.production && firebaseConfig.measurementId) {
                try {
                    this._analytics = getAnalytics(this.app);
                } catch (error) {
                    console.warn('Firebase Analytics initialization failed:', error);
                }
            }

            // Initialize Messaging (only if supported in browser)
            this.initializeMessaging();

            console.log('Firebase initialized successfully');
        } catch (error) {
            console.error('Firebase initialization error:', error);
            throw error;
        }
    }

    /**
     * Initialize Firebase Cloud Messaging if supported
     */
    private initializeMessaging(): void {
        isSupported().then(messagingSupported => {
            if (messagingSupported) {
                this._messaging = getMessaging(this.app!);
                console.log('Firebase Messaging initialized');
            } else {
                console.warn('Firebase Messaging is not supported in this browser');
            }
        }).catch(error => {
            console.warn('Firebase Messaging initialization failed:', error);
        });
    }

    /**
     * Get Firebase Auth instance
     */
    get auth(): Auth {
        return this._auth!;
    }

    /**
     * Get Firestore instance
     */
    get firestore(): Firestore {
        return this._firestore!;
    }

    /**
     * Get Firebase Messaging instance (may be null if not supported)
     */
    get messaging(): Messaging | null {
        return this._messaging;
    }

    /**
     * Get Firebase Analytics instance (may be null if not initialized)
     */
    get analytics(): Analytics | null {
        return this._analytics;
    }

    /**
     * Get Firebase App instance
     */
    get firebaseApp(): FirebaseApp {
        return this.app!;
    }
}
